import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import classnames from 'classnames';
import { createSwatchTitle } from 'src/components/BundleProduct/BundleSizesSelector';
import { HandleProductChangeType } from 'src/MixAndMatchBundle/MixAndMatchPickerRow';
import { MixAndMatchVariant } from 'src/MixAndMatchBundle/MixAndMatchProductContainer';
import {
    camelized_BundleConfigOptions_configurable_options_values,
    camelized_BundleConfigOptions_configurable_options_values_swatch_data,
} from 'src/queries/__fragments__/__generated__/BundleConfigOptions';
import {
    HandleBundleColorChangeType,
    HandleBundleSizeChangeType,
} from 'src/MixAndMatchBundle/MixAndMatchProductPicker';
import { swatchImage } from 'src/components/ProductDetail/utils/swatchData';

import { ProductStockStatus } from '../../__generated__/globalTypes';

import classes from './MixAndMatchSelector.scss';

type Props = {
    disabled?: boolean;
    title: 'Size' | 'Colour' | 'Select Product';
    options?: camelized_BundleConfigOptions_configurable_options_values[];
    productVariants?: MixAndMatchVariant[];
    handleBundleColorChange?: HandleBundleColorChangeType;
    handleBundleSizeChange?: HandleBundleSizeChangeType;
    handleProductChange?: HandleProductChangeType;
    index: number;
    productIndex?: number;
    className?: string;
    productId: number;
    newOptionPicked?: string | undefined;
    setNewOptionPicked: Dispatch<SetStateAction<string | undefined>>;
    setInputEnabled: Dispatch<SetStateAction<boolean>>;
    setSizeEnabled?: Dispatch<SetStateAction<boolean>>;
    productName?: string;
    handleProductValueChange?: (productId: number, value: number, type: 'colorId' | 'maxQty') => void;
    colorId?: number;
    bundleDetails?: any;
};

const swatchCss = (swatchData: camelized_BundleConfigOptions_configurable_options_values_swatch_data) => {
    if (swatchData.typename === 'ImageSwatchData')
        return { background: `url(${swatchImage(swatchData.value)}) center center`, backgroundSize: 'contain' };

    return { backgroundColor: swatchData.value };
};

const Swatch: React.FC<{ option: camelized_BundleConfigOptions_configurable_options_values }> = ({ option }) => (
    <span className={classes.swatch} style={swatchCss(option.swatchData)} data-value={option.label} />
);

const useOutsideClickDetector = (ref, setSelectorOpen: React.Dispatch<React.SetStateAction<boolean>>) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setSelectorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, setSelectorOpen]);
};

const SelectorWrapper: React.FC<React.PropsWithChildren<{ wrapperRef; length: number }>> = ({
    wrapperRef,
    children,
    length,
}) => (
    <div
        ref={wrapperRef}
        className={classnames(classes.selectorContent, {
            [classes.scroll]: length > 6,
        })}
    >
        {children}
    </div>
);

export const MixAndMatchSelector: React.FC<Props> = ({
    disabled,
    title,
    options,
    productVariants,
    handleBundleColorChange,
    handleBundleSizeChange,
    handleProductChange,
    index,
    productIndex,
    className,
    productId,
    newOptionPicked,
    setNewOptionPicked,
    setInputEnabled,
    setSizeEnabled,
    handleProductValueChange,
    colorId,
    bundleDetails,
}) => {
    const [selectorOpen, setSelectorOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [pickedVariant, setPickedVariant] = useState<MixAndMatchVariant>();
    const [pickedOption, setPickedOption] = useState<camelized_BundleConfigOptions_configurable_options_values>();
    const optionsHaveSwatches = Boolean(options?.[0]?.swatchData);
    useOutsideClickDetector(wrapperRef, setSelectorOpen);

    useEffect(() => {
        if (!newOptionPicked) return;

        if (newOptionPicked === 'Product') setPickedOption(undefined);
        if (newOptionPicked === 'Colour' && title === 'Size') setPickedOption(undefined);

        setNewOptionPicked(undefined);
    }, [newOptionPicked, title, setNewOptionPicked]);

    const variantsOfColor = bundleDetails?.bundleVariants?.filter(
        (variant) => variant.attributes.find((attribute) => attribute.code === 'color').value_index === colorId!,
    );

    const handleVariantClick = (variant: MixAndMatchVariant) => {
        setSelectorOpen(false);
        if (productIndex !== undefined) {
            setPickedVariant(variant);

            handleProductChange?.(
                productId,
                variant.id,
                variant.title,
                variant.value,
                Boolean(pickedVariant),
                variant.image,
                variant.mediaEntries,
            );
            setNewOptionPicked('Product');
            setInputEnabled(false);
            setSizeEnabled && setSizeEnabled(false);
        }
    };

    const handleOptionClick = (option: camelized_BundleConfigOptions_configurable_options_values, maxQty?: number) => {
        setSelectorOpen(false);
        if (productIndex !== undefined) {
            if (title === 'Colour') {
                handleBundleColorChange?.(option, index, productIndex, pickedOption ? productIndex : undefined);
                setInputEnabled(false);
                setSizeEnabled && setSizeEnabled(true);
                pickedOption && setNewOptionPicked('Colour');
                handleProductValueChange?.(productId, option.valueIndex, 'colorId');
            }
            if (title === 'Size') {
                handleBundleSizeChange?.({ id: option.valueIndex, quantity: 0 }, index, productIndex);
                setInputEnabled(true);
                pickedOption && setNewOptionPicked('Size');
                if (maxQty) {
                    handleProductValueChange?.(productId, maxQty, 'maxQty');
                }
            }

            setPickedOption(option);
        }
    };

    return (
        <div
            className={classnames(classes.selectorDisplay, className, {
                [classes.pickedOptionDisplay]: pickedOption,
                [classes.disabledSelector]: disabled,
            })}
            onClick={(event) => {
                if (
                    !disabled &&
                    (options || productVariants) &&
                    !wrapperRef?.current?.contains?.(event.currentTarget)
                ) {
                    setSelectorOpen(!selectorOpen);
                }
            }}
        >
            {pickedOption && optionsHaveSwatches && <Swatch option={pickedOption} />}
            <span
                className={classnames(classes.selectedOptionTitle, {
                    [classes.selectorOptionSizesTitle]: title === 'Size' && pickedOption,
                })}
            >
                {pickedOption
                    ? createSwatchTitle(pickedOption.label)
                    : pickedVariant
                      ? createSwatchTitle(pickedVariant.title)
                      : createSwatchTitle(title)}
            </span>
            <img
                className={classes.arrowDown}
                src={
                    !selectorOpen
                        ? require('../../static/icons/MixMatchArrowDown.png')
                        : require('../../static/icons/MixMatchArrowUp.png')
                }
                alt="Arrow Down"
            />
            {!disabled && selectorOpen && options && (
                <SelectorWrapper wrapperRef={wrapperRef} length={options.length}>
                    {options.map((option) => {
                        let stockQty = undefined;
                        let isInStock = true;

                        if (title === 'Size') {
                            const currentVariant = variantsOfColor.find(
                                (variant) =>
                                    variant.attributes.find((attribute) => attribute.code === 'size').value_index ===
                                    option.valueIndex,
                            );

                            const isCurrentOption =
                                currentVariant?.attributes.find((attribute) => attribute.code === 'size')
                                    .value_index === option.valueIndex;

                            stockQty = currentVariant?.product?.stock_qty;
                            isInStock = currentVariant?.product?.stock_status === ProductStockStatus.IN_STOCK;

                            if (!isCurrentOption) {
                                return null;
                            }
                        }

                        return (
                            <div
                                key={option.label}
                                className={classnames(classes.selectorOption, {
                                    [classes.selectorOptionSizes]: title === 'Size',
                                    [classes.disabled]: title === 'Size' && !isInStock,
                                })}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (isInStock) {
                                        handleOptionClick(option, stockQty);
                                    }
                                }}
                            >
                                {optionsHaveSwatches && <Swatch option={option} />}
                                {createSwatchTitle(option?.label)}
                                {typeof stockQty !== 'undefined' ? (stockQty > 100 ? ' (100+)' : ` (${stockQty})`) : ''}
                            </div>
                        );
                    })}
                </SelectorWrapper>
            )}

            {!disabled && selectorOpen && productVariants && (
                <SelectorWrapper wrapperRef={wrapperRef} length={productVariants.length}>
                    {productVariants.map((variant) => (
                        <div
                            key={variant.title}
                            className={classnames(classes.selectorOption, {
                                [classes.selectorOptionWithPreview]: variant.image,
                            })}
                            onClick={(event) => {
                                event.stopPropagation();
                                handleVariantClick(variant);
                            }}
                        >
                            <img className={classes.variantImage} src={variant.image} alt={variant.title} />
                            {createSwatchTitle(variant.title)}
                        </div>
                    ))}
                </SelectorWrapper>
            )}
        </div>
    );
};
